import type { AgentName, AgentStatus as AgentStatusType } from "../../../../shared/types";
import { cn } from "../../lib/utils";

interface AgentStatusProps {
  name: AgentName;
  status: AgentStatusType;
}

const agentLabels: Record<AgentName, string> = {
  intake: "Intake",
  ideation: "Ideation",
  content: "Content",
  repurpose: "Repurpose",
  distribution: "Distribution",
  learning: "Learning",
};

function StatusIcon({ status }: { status: AgentStatusType }) {
  switch (status) {
    case "queued":
      return (
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-200">
          <span className="h-2 w-2 rounded-full bg-slate-400" />
        </span>
      );
    case "working":
      return (
        <span className="relative inline-flex h-6 w-6 items-center justify-center">
          <span className="absolute h-6 w-6 animate-ping rounded-full bg-blue-200 opacity-75" />
          <span className="relative h-3 w-3 rounded-full bg-blue-500" />
        </span>
      );
    case "draft":
      return (
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-100">
          <svg
            className="h-3.5 w-3.5 text-amber-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
            />
          </svg>
        </span>
      );
    case "ready":
      return (
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
          <svg
            className="h-3.5 w-3.5 text-green-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M5 13l4 4L19 7"
            />
          </svg>
        </span>
      );
    case "failed":
      return (
        <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-red-100">
          <svg
            className="h-3.5 w-3.5 text-red-600"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </span>
      );
  }
}

export function AgentStatus({ name, status }: AgentStatusProps) {
  return (
    <div className="flex flex-col items-center gap-1.5">
      <StatusIcon status={status} />
      <span
        className={cn(
          "text-xs font-medium",
          status === "working" && "text-blue-600",
          status === "draft" && "text-amber-600",
          status === "ready" && "text-green-600",
          status === "failed" && "text-red-600",
          (status === "queued") && "text-slate-400",
        )}
      >
        {agentLabels[name]}
      </span>
    </div>
  );
}
